import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

// Define possible Nord Modal sizes
type NordModalSize = 's' | 'm' | 'l' | 'auto';

// Define the props for our wrapper component
interface AppNordModalProps {
  open?: boolean;
  size?: NordModalSize;
  heading?: string;
  preventClose?: boolean;
  closeOnEsc?: boolean;
  closeOnOutsideClick?: boolean;
  className?: string;
  children?: React.ReactNode;
  onClose?: () => void;
  onAfterClose?: () => void;
  [key: string]: any; // Allow passing other attributes
}

/**
 * A wrapper around the Nord Design System <nord-modal> custom element.
 * Provides type safety and integrates with Tailwind CSS utility functions.
 */
export const AppNordModal: React.FC<AppNordModalProps> = ({
  open = false,
  size = 'm',
  heading,
  preventClose = false,
  closeOnEsc = true,
  closeOnOutsideClick = true,
  className,
  children,
  onClose,
  onAfterClose,
  ...props // Collect remaining props
}) => {
  // Reference to the modal element
  const modalRef = useRef<HTMLElement | null>(null);

  // Combine classes using cn
  const combinedClassName = cn(
    // Add any base classes for styling the wrapper if needed
    className
  );

  // Set up event listeners for the modal
  useEffect(() => {
    const modalElement = modalRef.current;
    
    if (modalElement) {
      // Add event listeners
      if (onClose) {
        modalElement.addEventListener('n-close', onClose);
      }
      
      if (onAfterClose) {
        modalElement.addEventListener('n-after-close', onAfterClose);
      }
      
      // Clean up event listeners
      return () => {
        if (onClose) {
          modalElement.removeEventListener('n-close', onClose);
        }
        
        if (onAfterClose) {
          modalElement.removeEventListener('n-after-close', onAfterClose);
        }
      };
    }
  }, [onClose, onAfterClose]);

  // Update open state when prop changes
  useEffect(() => {
    const modalElement = modalRef.current as any; 
    if (modalElement) {
      // Control visibility via the 'open' attribute/property
      modalElement.open = open;
    }
  }, [open]);

  // Type assertion to help TypeScript understand the custom element attributes
  const elementProps: any = {
    size,
    heading,
    class: combinedClassName,
    ...props,
  };

  // Add boolean attributes only if they're true or explicitly set
  if (preventClose) {
    elementProps['prevent-close'] = 'true';
  }

  if (!closeOnEsc) {
    elementProps['close-on-esc'] = 'false';
  }

  if (!closeOnOutsideClick) {
    elementProps['close-on-outside-click'] = 'false';
  }

  return (
    <nord-modal 
      ref={modalRef as any} 
      {...elementProps}
    >
      {children}
    </nord-modal>
  );
}; 